import moment from "moment";
import { getAppointmentPhase } from "../enumutils";

export interface NewFlightFormState {
  artist: server.dto.ArtistSmall | undefined
  passengers: server.dto.ContactSmallV2[]
  phase: server.dto.AppointmentPhase

  travelAgency: string
  notes: string
  airline: server.dto.Airline | undefined
  bookingCode: string

  flightDesignator: string

  arrivalAirport: server.dto.Airport | undefined
  arrivalLocal: moment.Moment
  arrivalTerminal: string

  departureAirport: server.dto.Airport | undefined
  departureLocal: moment.Moment
  departureTerminal: string

  access: string
  capacity: number | undefined
  confirmationNumber: string
}

export const getInitialNewFlightFormState = (
    serverReference: server.dto.ServerReference,
    fromFlight: server.dto.FlightSidePanelData | undefined,
    artist: server.dto.ArtistSmall | undefined,
    date: moment.Moment | undefined,
): NewFlightFormState => {
  const previousLegDate = fromFlight?.flight.dateTime.endDisplay
    ? moment.utc(fromFlight.flight.dateTime.endDisplay)
    : (date ?? moment.utc());

  return {
    artist: fromFlight?.flight.artist ?? artist,
    passengers: fromFlight?.passengers.map(p => p.contact) ?? [],
    phase: getAppointmentPhase(fromFlight?.flight.phase ?? "confirmed", serverReference),

    travelAgency: fromFlight?.flight.travelAgency ?? "",
    notes: fromFlight?.flight.notes ?? "",
    airline: fromFlight?.flight.airline ?? undefined,
    flightDesignator: "",
    bookingCode: "",

    arrivalAirport: undefined,
    arrivalLocal: previousLegDate.clone(),
    arrivalTerminal: "",

    departureAirport: undefined,
    departureLocal: previousLegDate.clone(),
    departureTerminal: "",

    access: "",
    capacity: undefined,
    confirmationNumber: fromFlight?.flight.confirmationNumber ?? "",
  };
};

// New passengers inherit booking details from the matching passenger on the previous leg.
const getNewFlightPassengerInstruction = (
    contact: server.dto.ContactSmallV2,
    bookingCode: string,
    fromFlight: server.dto.FlightSidePanelData | undefined,
) => {
  const passengerFromPreviousLeg = fromFlight?.passengers.find(passenger => passenger.contact.contactId === contact.contactId);
  return {
    baggage: passengerFromPreviousLeg?.baggage ?? "",
    bookingReference: passengerFromPreviousLeg?.bookingReference ?? bookingCode,
    contact: contact.contactId,
    flightId: 0,
    meal: passengerFromPreviousLeg?.meal ?? "",
    seat: "",
    ticketNumber: passengerFromPreviousLeg?.ticketNumber ?? "",
    travelClass: passengerFromPreviousLeg?.travelClass ?? "unknown",
  };
};

// Return type is inferred: the generated CreateFlightInstruction types artistId and the
// airport ids as non-null, but the code passes undefined for them when unset.
export const getCreateFlightInstruction = (
    state: NewFlightFormState,
    clientId: number,
    flightType: server.dto.FlightTypes,
    linkToShowId: number | undefined,
    fromFlight: server.dto.FlightSidePanelData | undefined,
) => ({
  access: state.access,
  airlineId: state.airline?.airlineId || 0,
  arrivalAirportId: state.arrivalAirport?.airportId,
  arrivalLocal: state.arrivalLocal.toDate(),
  arrivalTerminal: state.arrivalTerminal,
  artistId: state.artist?.artistId,
  clientId,
  passengers: state.passengers.map(p => getNewFlightPassengerInstruction(p, state.bookingCode, fromFlight)),
  capacity: state.capacity ?? 0,
  confirmationNumber: state.confirmationNumber,
  departureAirportId: state.departureAirport?.airportId,
  departureLocal: state.departureLocal.toDate(),
  departureTerminal: state.departureTerminal,
  flightDesignator: state.flightDesignator,
  flightType,
  linkToShowId,
  notes: state.notes,
  phase: state.phase.value,
  travelAgency: state.travelAgency,
});
